import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  FileEdit,
  Radar,
  FolderOpen,
  Building2,
  ClipboardCheck,
  Presentation,
  Users2,
  Database,
  BarChart3,
  ChevronRight,
  MousePointer2,
  Sparkles,
  BookOpen,
  ArrowRight,
  Monitor,
  Zap
} from 'lucide-react';

interface DemoFeature {
  id: string;
  icon: React.ElementType;
  title: string;
  description: string;
  steps: string[];
  route: string;
  color: string;
  animationDelay: number;
}

const features: DemoFeature[] = [
  {
    id: 'grant-monitoring',
    icon: Radar,
    title: '공모사업 모니터링',
    description: '13개 정부 기관의 공모사업을 자동 수집하고 D-day 알림을 받으세요.',
    steps: ['공모사업 목록 확인', '기관/분야별 필터링', '북마크 저장', 'D-day 알림 설정'],
    route: '/grant-monitoring',
    color: 'from-blue-500 to-cyan-500',
    animationDelay: 0,
  },
  {
    id: 'proposal-generator',
    icon: FileEdit,
    title: '사업계획서 작성',
    description: 'AI가 맥락표를 분석하여 고품질 사업계획서를 자동 생성합니다.',
    steps: ['공모사업 선택', '참여 기업 선택', 'AI 맥락표 자동생성', 'AI 본문 생성', '평가 및 저장'],
    route: '/proposal-generator',
    color: 'from-emerald-500 to-teal-500',
    animationDelay: 100,
  },
  {
    id: 'proposals',
    icon: FolderOpen,
    title: '제안서 관리',
    description: '작성된 제안서의 진행 상태를 추적하고 관리합니다.',
    steps: ['제안서 목록 조회', '상태별 필터링', '편집/복사/삭제', '제출 현황 확인'],
    route: '/proposals',
    color: 'from-purple-500 to-indigo-500',
    animationDelay: 200,
  },
  {
    id: 'company-pool',
    icon: Building2,
    title: '기업/기관 데이터풀',
    description: '참여 기업 정보를 등록하고 관리합니다.',
    steps: ['기업 검색', '기업 상세 정보 확인', '신규 기업 등록', '포트폴리오 관리'],
    route: '/company-pool',
    color: 'from-orange-500 to-amber-500',
    animationDelay: 300,
  },
  {
    id: 'evaluation',
    icon: ClipboardCheck,
    title: '가상 평가',
    description: 'AI가 실제 심사위원처럼 제안서를 사전 평가하고 개선점을 제시합니다.',
    steps: ['평가할 제안서 선택', 'AI 평가 실행', '항목별 점수 확인', '약점 및 개선 권고 확인'],
    route: '/evaluation',
    color: 'from-red-500 to-pink-500',
    animationDelay: 400,
  },
  {
    id: 'ppt-generator',
    icon: Presentation,
    title: 'PPT 자동 생성',
    description: '제안서 내용을 기반으로 발표용 PPT를 자동 생성합니다.',
    steps: ['제안서 선택', '템플릿 선택', '색상/옵션 설정', 'AI PPT 생성', 'PPTX 다운로드'],
    route: '/ppt-generator',
    color: 'from-violet-500 to-purple-500',
    animationDelay: 500,
  },
  {
    id: 'regional-knowhow',
    icon: Database,
    title: '지역 노하우',
    description: '지역별 공모사업 작성 노하우와 성공 사례를 검색합니다.',
    steps: ['키워드/지역 검색', 'RAG 기반 유사 사례 조회', '노하우 분석 결과 확인'],
    route: '/regional-knowhow',
    color: 'from-teal-500 to-green-500',
    animationDelay: 600,
  },
  {
    id: 'matching',
    icon: Users2,
    title: '매칭 & 컨소시엄',
    description: '공모사업에 적합한 기업/기관을 매칭하고 컨소시엄을 구성합니다.',
    steps: ['공모사업 기반 매칭 조회', '기업 역량 비교', '컨소시엄 구성'],
    route: '/matching',
    color: 'from-sky-500 to-blue-500',
    animationDelay: 700,
  },
];

// Animated cursor demo component
const AnimatedDemo: React.FC<{ feature: DemoFeature; isActive: boolean }> = ({ feature, isActive }) => {
  const [cursorStep, setCursorStep] = useState(0);

  React.useEffect(() => {
    if (!isActive) {
      setCursorStep(0);
      return;
    }
    const interval = setInterval(() => {
      setCursorStep(prev => (prev + 1) % (feature.steps.length + 1));
    }, 1500);
    return () => clearInterval(interval);
  }, [isActive, feature.steps.length]);

  // Step positions as percentages
  const positions = feature.steps.map((_, i) => ({
    x: 15 + (i % 3) * 30,
    y: 25 + Math.floor(i / 3) * 35,
  }));

  const currentPos = cursorStep < positions.length
    ? positions[cursorStep]
    : { x: 50, y: 50 };

  return (
    <div className="relative w-full h-48 bg-gray-900 rounded-lg overflow-hidden">
      {/* Simulated UI elements */}
      <div className="absolute inset-0 p-3">
        {/* Title bar */}
        <div className="flex items-center space-x-1 mb-2">
          <div className="w-2 h-2 rounded-full bg-red-400" />
          <div className="w-2 h-2 rounded-full bg-yellow-400" />
          <div className="w-2 h-2 rounded-full bg-green-400" />
          <span className="text-[10px] text-gray-400 ml-2">{feature.title}</span>
        </div>

        {/* Simulated step blocks */}
        {feature.steps.map((step, i) => {
          const isHighlighted = cursorStep === i;
          const col = i % 3;
          const row = Math.floor(i / 3);
          return (
            <div
              key={i}
              className={`absolute transition-all duration-500 ${
                isHighlighted ? 'ring-2 ring-emerald-400 scale-105' : 'opacity-60'
              }`}
              style={{
                left: `${8 + col * 31}%`,
                top: `${22 + row * 38}%`,
                width: '28%',
                padding: '6px',
              }}
            >
              <div className={`rounded p-1.5 ${isHighlighted ? 'bg-emerald-500/30' : 'bg-gray-700/50'}`}>
                <div className="text-[9px] text-gray-300 truncate">{step}</div>
                <div className={`h-1 rounded-full mt-1 ${isHighlighted ? 'bg-emerald-400' : 'bg-gray-600'}`}
                  style={{ width: isHighlighted ? '100%' : '40%' }}
                />
              </div>
            </div>
          );
        })}

        {/* Active step label */}
        {cursorStep < feature.steps.length && (
          <div className="absolute bottom-2 left-3 right-3">
            <div className="bg-emerald-500/20 border border-emerald-500/30 rounded px-2 py-1">
              <span className="text-[10px] text-emerald-300">
                Step {cursorStep + 1}: {feature.steps[cursorStep]}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Animated cursor */}
      {isActive && (
        <div
          className="absolute transition-all duration-700 ease-in-out pointer-events-none z-10"
          style={{
            left: `${currentPos.x}%`,
            top: `${currentPos.y}%`,
          }}
        >
          <MousePointer2
            className="h-5 w-5 text-emerald-400 drop-shadow-[0_0_6px_rgba(16,185,129,0.6)]"
            fill="rgba(16,185,129,0.2)"
          />
          {/* Click ripple */}
          <div className="absolute -inset-2 rounded-full bg-emerald-400/20 animate-ping" />
        </div>
      )}
    </div>
  );
};

const DemoGuide: React.FC = () => {
  const navigate = useNavigate();
  const [activeDemo, setActiveDemo] = useState<string | null>(null);
  const [selectedFeature, setSelectedFeature] = useState<DemoFeature | null>(null);

  const handleStartTour = (feature: DemoFeature) => {
    // Navigate to the page and activate tour (via URL param)
    navigate(`${feature.route}?tour=true`);
  };

  return (
    <div className="space-y-8">
      {/* 헤더 */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
          <BookOpen className="h-4 w-4 mr-2" />
          사용 가이드 & 데모
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          ProposalCraft AI 사용법
        </h1>
        <p className="text-lg text-gray-600">
          AI 기반 공모사업 제안서 자동 생성 플랫폼의 주요 기능을 살펴보세요.
          <br />
          각 기능의 데모를 확인하거나, 실제 페이지에서 인터랙티브 투어를 시작할 수 있습니다.
        </p>
      </div>

      {/* 전체 워크플로우 */}
      <div className="card bg-gradient-to-r from-gray-900 to-gray-800 text-white p-8 rounded-2xl">
        <h2 className="text-xl font-semibold mb-6 flex items-center">
          <Zap className="h-6 w-6 mr-2 text-yellow-400" />
          전체 워크플로우
        </h2>
        <div className="flex items-center justify-between">
          {[
            { icon: Radar, label: '공고 발견', sub: '13개 기관 자동 수집' },
            { icon: FileEdit, label: '맥락표 생성', sub: 'AI 자동 작성' },
            { icon: Sparkles, label: '제안서 생성', sub: 'Claude AI 활용' },
            { icon: ClipboardCheck, label: '가상 평가', sub: 'AI 사전 심사' },
            { icon: Presentation, label: '발표자료', sub: 'PPT 자동 생성' },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center text-center flex-1">
                <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center mb-2">
                  <item.icon className="h-7 w-7 text-emerald-400" />
                </div>
                <p className="text-sm font-medium">{item.label}</p>
                <p className="text-xs text-gray-400 mt-1">{item.sub}</p>
              </div>
              {i < 4 && (
                <ArrowRight className="h-5 w-5 text-gray-500 flex-shrink-0 mx-2" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* 기능별 데모 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {features.map((feature) => {
          const Icon = feature.icon;
          const isActive = activeDemo === feature.id;

          return (
            <div
              key={feature.id}
              className="card border border-gray-200 hover:border-emerald-300 hover:shadow-lg transition-all rounded-xl overflow-hidden"
              style={{ animationDelay: `${feature.animationDelay}ms` }}
            >
              {/* Demo animation area */}
              <div
                className="cursor-pointer"
                onMouseEnter={() => setActiveDemo(feature.id)}
                onMouseLeave={() => setActiveDemo(null)}
              >
                <AnimatedDemo feature={feature} isActive={isActive} />
              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex items-center space-x-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{feature.description}</p>

                {/* Steps */}
                <div className="space-y-1.5 mb-4">
                  {feature.steps.map((step, i) => (
                    <div key={i} className="flex items-center space-x-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleStartTour(feature)}
                    className="flex items-center px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <Play className="h-4 w-4 mr-1.5" />
                    인터랙티브 투어
                  </button>
                  <button
                    onClick={() => navigate(feature.route)}
                    className="flex items-center px-4 py-2 text-gray-700 border border-gray-300 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Monitor className="h-4 w-4 mr-1.5" />
                    페이지로 이동
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 하단 안내 */}
      <div className="text-center py-8">
        <p className="text-gray-500 text-sm">
          모든 데이터는 실제 서버에서 가져옵니다. 데모 페이지에서만 시뮬레이션 애니메이션이 표시됩니다.
        </p>
      </div>
    </div>
  );
};

export default DemoGuide;
